// student-nav.tsx — the student area's entry point to <AppHeader>.
//
// The bar itself is shared with admin and moderator (features/shell/app-header).
// What stays here is student-specific: which views exist, the level chip, and
// the family-device rule that replaces logout with "switch profile".
import { useTranslation } from 'react-i18next';
import { authService } from '../../../services/authentication/auth-service';
import { hasFamilyDeviceToken } from '../../../services/family-device-storage';
import { isStudentCheatsEnabled } from '../../../config/environment';
import defaultAvatar from '../../../assets/icons/avatars/default-avatar.svg';
import { AppHeader } from '../../shell/app-header';
import type { StudentView } from '../student-dashboard';

interface StudentNavProps {
  activeView: StudentView;
  onNavigate: (view: StudentView) => void;
  userName: string;
  firstName?: string;
  avatarPhotoUrl: string | null;
  level?: number;
}

const NAV_ITEMS: { view: StudentView; labelKey: string }[] = [
  { view: 'home',      labelKey: 'student.nav.home'      },
  { view: 'campaigns', labelKey: 'student.nav.campaigns' },
  { view: 'sessions',  labelKey: 'student.nav.sessions'  },
  { view: 'portfolio', labelKey: 'student.nav.portfolio' },
  // Dev tooling — only offered in local/develop environments
  ...(isStudentCheatsEnabled()
    ? [{ view: 'cheats' as const, labelKey: 'student.nav.cheats' }]
    : []),
];

export function StudentNav({ activeView, onNavigate, userName, firstName: firstNameProp, avatarPhotoUrl, level }: StudentNavProps) {
  const { t } = useTranslation();
  const firstName = firstNameProp || userName.split(' ')[0];

  // Family devices have NO logout here — leaving the account happens on the
  // profile picker (confirmed logout there unlinks the device). The plain logout
  // only remains for non-family logins (username).
  const isFamilyDevice = hasFamilyDeviceToken();

  return (
    <AppHeader
      onBrandClick={() => onNavigate('home')}
      nav={NAV_ITEMS.map(({ view, labelKey }) => ({
        key: view,
        label: t(labelKey),
        active: activeView === view,
        onClick: () => onNavigate(view),
      }))}
      badge={typeof level === 'number' ? `◆ ${t('student.hero.level', { level })}` : undefined}
      user={{ name: firstName, avatarUrl: avatarPhotoUrl || defaultAvatar }}
      onProfileClick={() => onNavigate('portfolio')}
      profileActive={activeView === 'portfolio'}
      onSignOut={() =>
        isFamilyDevice ? void authService.switchProfile() : void authService.logout()
      }
      signOutLabel={isFamilyDevice ? t('dashboard.switchProfile') : t('dashboard.logout')}
    />
  );
}
